import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Fade,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { manualQuestionApi } from "../../api/manualQuestionApi";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import QuizIcon from "@mui/icons-material/Quiz";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

// ─── Brand palette ─────────────────────────────────────────────────────────────
const BRAND = {
  grad: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)", 
  gradHover: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
  solid: "#7c3aed",
  soft: "#f5f3ff",
  border: "#ddd6fe",
};

// ─── Difficulty badge config ───────────────────────────────────────────────────
const DIFFICULTY_CONFIG = {
  easy: { bg: "#ecfdf5", text: "#059669", dot: "#10b981" },
  medium: { bg: "#fffbeb", text: "#d97706", dot: "#f59e0b" },
  moderate: { bg: "#fffbeb", text: "#d97706", dot: "#f59e0b" },
  hard: { bg: "#fef2f2", text: "#dc2626", dot: "#ef4444" },
  expert: { bg: "#f0f9ff", text: "#0284c7", dot: "#0ea5e9" },
};

const DIFFICULTY_OPTIONS = ["Easy", "Medium", "Hard", "Expert"];

const getDifficultyConfig = (diff = "") => {
  const key = diff.toLowerCase();
  for (const [k, v] of Object.entries(DIFFICULTY_CONFIG)) {
    if (key.includes(k)) return v;
  }
  return { bg: "#f8fafc", text: "#64748b", dot: "#cbd5e1" };
};

// ─── Skill color cycling ───────────────────────────────────────────────────────
const SKILL_PALETTES = [
  { bg: "#fdf2f8", text: "#db2777", border: "#fbcfe8" }, // Soft Pink
  { bg: "#fff7ed", text: "#ea580c", border: "#fed7aa" }, // Soft Orange
  { bg: "#ecfdf5", text: "#059669", border: "#a7f3d0" }, // Soft Green
  { bg: "#f0fdfa", text: "#0d9488", border: "#99f6e4" }, // Soft Teal
  { bg: "#eff6ff", text: "#2563eb", border: "#bfdbfe" }, // Soft Blue
];

const getSkillPalette = (skill = "") => {
  if (!skill) return { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" };
  let hash = 0;
  for (let i = 0; i < skill.length; i++) hash = skill.charCodeAt(i) + ((hash << 5) - hash);
  return SKILL_PALETTES[Math.abs(hash) % SKILL_PALETTES.length];
};

// ─── Shared text field style ───────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.85rem",
    backgroundColor: "#fff",
    "&:hover fieldset": { borderColor: "#c4b5fd" },
    "&.Mui-focused fieldset": { borderColor: "#8b5cf6", borderWidth: 1.5 },
    "& textarea::placeholder, & input::placeholder": { opacity: 0.6, color: "#64748b" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.85rem", color: "#64748b" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#7c3aed" },
  "& .MuiFormHelperText-root": { fontSize: "0.7rem", ml: 0.25 },
};

const RequiredLabel = ({ label }) => (
  <span>
    {label} <span style={{ color: "#ef4444", fontWeight: 800 }}>*</span>
  </span>
);

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = ({ onAdd }) => (
  <Fade in>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 3,
        textAlign: "center",
        borderRadius: "14px",
        border: "1.5px dashed #cbd5e1",
        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "14px",
          background: BRAND.grad,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          boxShadow: "0 6px 16px rgba(139,92,246,0.25)",
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: 24, color: "#fff" }} />
      </Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1e293b", mb: 0.5, letterSpacing: "-0.02em" }}>
        No questions yet
      </Typography>
      <Typography variant="body2" sx={{ color: "#64748b", maxWidth: 300, lineHeight: 1.6, mb: 3 }}>
        Add custom interview questions tied to this role with expected answers for consistent evaluations.
      </Typography>
      <Button
        variant="contained"
        disableElevation
        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
        onClick={onAdd}
        sx={{
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 600,
          px: 2.5,
          py: 1,
          background: BRAND.grad,
          boxShadow: "0 4px 12px rgba(139,92,246,0.3)",
          "&:hover": { background: BRAND.gradHover, boxShadow: "0 6px 16px rgba(139,92,246,0.4)" },
        }}
      >
        Add First Question
      </Button>
    </Box>
  </Fade>
);

// ─── Question Card (List View) ────────────────────────────────────────────────
const QuestionCard = ({ q, index, onEdit, onDelete, onView }) => {
  const diffCfg = getDifficultyConfig(q.difficulty);
  const skillPalette = getSkillPalette(q.skill);

  return (
    <Fade in timeout={150 + index * 50}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          background: "#fff",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "#cbd5e1",
            boxShadow: "0 4px 20px rgba(15,23,42,0.04)",
          },
        }}
      >
        <Box sx={{ height: 3, background: BRAND.grad }} />

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
            <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#94a3b8", mr: 1 }}>
                  {String(index + 1).padStart(2, "0")}
                </Typography>

                {q.skill && (
                  <Chip
                    label={q.skill}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      bgcolor: skillPalette.bg,
                      color: skillPalette.text,
                      border: `1px solid ${skillPalette.border}`,
                      borderRadius: "6px",
                    }}
                  />
                )}

                {q.difficulty && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: "6px",
                      background: diffCfg.bg,
                      border: `1px solid ${alpha(diffCfg.dot, 0.2)}`,
                    }}
                  >
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: diffCfg.dot, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: diffCfg.text }}>
                      {q.difficulty}
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: "#1e293b",
                  lineHeight: 1.5,
                }}
              >
                {q.question}
              </Typography>

              {(q.ideal_answer || q.answer) && (
                <Box
                  sx={{
                    p: "12px 16px",
                    borderRadius: "8px",
                    background: BRAND.soft,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                  }}
                >
                  <LightbulbOutlinedIcon sx={{ fontSize: 18, color: BRAND.solid, mt: "2px", flexShrink: 0 }} />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 800,
                        color: BRAND.solid,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        mb: 0.5,
                      }}
                    >
                      Expected Answer
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: "#475569",
                        lineHeight: 1.6,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {q.ideal_answer || q.answer}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Stack>

            <Stack direction="column" spacing={0.75} sx={{ flexShrink: 0 }}>
              <Tooltip title="View Details" placement="left">
                <IconButton
                  size="small"
                  onClick={() => onView(q)}
                  sx={{
                    width: 32,
                    height: 32,
                    border: "1px solid #f1f5f9",
                    borderRadius: "8px",
                    color: "#64748b",
                    bgcolor: "#fff",
                    "&:hover": { bgcolor: "#f0f9ff", borderColor: "#bae6fd", color: "#0284c7" },
                  }}
                >
                  <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit" placement="left">
                <IconButton
                  size="small"
                  onClick={() => onEdit(q)}
                  sx={{
                    width: 32,
                    height: 32,
                    border: "1px solid #f1f5f9",
                    borderRadius: "8px",
                    color: "#64748b",
                    bgcolor: "#fff",
                    "&:hover": { bgcolor: "#f5f3ff", borderColor: "#ddd6fe", color: "#7c3aed" },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" placement="left">
                <IconButton
                  size="small"
                  onClick={() => onDelete(q.id)}
                  sx={{
                    width: 32,
                    height: 32,
                    border: "1px solid #f1f5f9",
                    borderRadius: "8px",
                    color: "#64748b",
                    bgcolor: "#fff",
                    "&:hover": { bgcolor: "#fef2f2", borderColor: "#fecaca", color: "#dc2626" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Paper>
    </Fade>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const QuestionsPage = () => {
  const location = useLocation();
  const jdId = location.state?.jdId;

  const [questions, setQuestions] = useState([]);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [skill, setSkill] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Dialog states
  const [openAddEdit, setOpenAddEdit] = useState(false);
  const [touched, setTouched] = useState(false);

  const [openView, setOpenView] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);

  // Toggle state: 'list' or 'table'
  const [viewMode, setViewMode] = useState("table");

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await manualQuestionApi.getQuestions(jdId);
      if (response?.status === 200) setQuestions(response.data || []);
    } catch (err) {
      console.error("Failed to fetch questions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jdId) fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jdId]);

  const handleOpenAdd = () => {
    setEditingId(null);
    setQuestion("");
    setAnswer("");
    setSkill("");
    setDifficulty("");
    setTouched(false);
    setOpenAddEdit(true);
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const errors = {
    question: !question.trim(),
    answer: !answer.trim(),
    skill: !skill.trim(),
    difficulty: !difficulty.trim(),
  };
  const isFormValid = !errors.question && !errors.answer && !errors.skill && !errors.difficulty;

  const handleSave = async () => {
    setTouched(true);
    if (!isFormValid) return;

    setIsSaving(true);
    const payload = { question, answer, job_id: jdId, skill, difficulty, status: "published" };

    try {
      const response = editingId
        ? await manualQuestionApi.updateQuestion(editingId, payload)
        : await manualQuestionApi.createQuestion(payload);
      if (response?.status === 201 || response?.status === 200) {
        setOpenAddEdit(false);
        setEditingId(null);
        setQuestion(""); setAnswer(""); setSkill(""); setDifficulty("");
        setTouched(false);
        await fetchQuestions();
      }
    } catch (err) {
      console.error("Error saving question:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setQuestion(item.question);
    setAnswer(item.ideal_answer || item.answer || "");
    setSkill(item.skill || "");
    setDifficulty(item.difficulty || "");
    setTouched(false);
    setOpenAddEdit(true);
  };

  const handleView = (item) => {
    setViewingItem(item);
    setOpenView(true);
  };

  // Helper to jump from View dialog straight to Edit dialog
  const handleEditFromView = () => {
    if (viewingItem) {
      setOpenView(false);
      handleEdit(viewingItem);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;
    try {
      const response = await manualQuestionApi.deleteQuestion(id);
      if (response?.status === 201 || response?.status === 200) fetchQuestions();
    } catch (err) {
      console.error("Error deleting question:", err);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", p: { xs: 2, sm: 4 } }}>
      <Box sx={{ maxWidth: 1100, mx: "auto", width: "100%" }}>

        {/* ── Page Header ── */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
            p: 2.5,
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            background: "#fff",
            boxShadow: "0 2px 10px rgba(15,23,42,0.02)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: BRAND.grad,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 14px rgba(139,92,246,0.3)",
                flexShrink: 0,
              }}
            >
              <QuizIcon sx={{ fontSize: 22, color: "#fff" }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#0f172a", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                Interview Questions
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500, mt: 0.25 }}>
                {questions.length > 0
                  ? `${questions.length} question${questions.length !== 1 ? "s" : ""} configured`
                  : "No questions added yet"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            {/* View Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              size="small"
              sx={{
                height: 40,
                bgcolor: "#f8fafc",
                "& .MuiToggleButton-root": {
                  borderColor: "#e2e8f0",
                  color: "#64748b",
                  px: 2,
                  "&.Mui-selected": {
                    color: BRAND.solid,
                    bgcolor: BRAND.soft,
                    borderColor: BRAND.border,
                    "&:hover": { bgcolor: BRAND.soft },
                  },
                },
              }}
            >
              <Tooltip title="List View">
                <ToggleButton value="list">
                  <ViewListOutlinedIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Table View">
                <ToggleButton value="table">
                  <TableRowsOutlinedIcon fontSize="small" />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              disableElevation
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleOpenAdd}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                height: 40,
                fontSize: "0.85rem",
                background: BRAND.grad,
                boxShadow: "0 3px 10px rgba(139,92,246,0.25)",
                "&:hover": { background: BRAND.gradHover, boxShadow: "0 5px 15px rgba(139,92,246,0.35)" },
              }}
            >
              Add Question
            </Button>
          </Stack>
        </Paper>

        {/* ── Stats bar ── */}
        {!loading && questions.length > 0 && (
          <Fade in>
            <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
              {["Easy", "Medium", "Hard"].map((level) => {
                const count = questions.filter((q) =>
                  (q.difficulty || "").toLowerCase().includes(level.toLowerCase())
                ).length;
                const cfg = getDifficultyConfig(level);
                return (
                  <Box
                    key={level}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderRadius: "8px",
                      background: cfg.bg,
                      border: `1px solid ${alpha(cfg.dot, 0.2)}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.dot }} />
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: cfg.text }}>
                      {count} {level}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Fade>
        )}

        {/* ── Content ── */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress size={32} thickness={4} sx={{ color: BRAND.solid }} />
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                Loading questions…
              </Typography>
            </Stack>
          </Box>
        ) : questions.length === 0 ? (
          <EmptyState onAdd={handleOpenAdd} />
        ) : viewMode === "list" ? (
          <Stack spacing={2}>
            {questions.map((q, i) => (
              <QuestionCard
                key={q.id}
                q={q}
                index={i}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            ))}
          </Stack>
        ) : (
          <Fade in>
           <TableContainer
  component={Paper}
  elevation={0}
  sx={{
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
    bgcolor: "#fff",
  }}
>
  <Table>

    {/* HEADER */}
    <TableHead>
      <TableRow
        sx={{
          bgcolor: "#f8fafc",
          "& th": {
            borderBottom: "2px solid #e2e8f0",
          },
        }}
      >
        {["#", "Skill", "Difficulty", "Question", "Expected Answer", "Actions"]
          .map((head) => (
            <TableCell
              key={head}
              sx={{
                py: 2.2,
                fontSize: "12px",
                fontWeight: 800,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              {head}
            </TableCell>
          ))}
      </TableRow>
    </TableHead>

    {/* BODY */}
    <TableBody>
      {questions.map((q, index) => {
        const diffCfg = getDifficultyConfig(q.difficulty);
        const skillPalette = getSkillPalette(q.skill);

        return (
          <TableRow
            key={q.id}
            hover
            sx={{
              transition: "all .2s",

              "& td": {
                borderBottom: "1px solid #e2e8f0",
              },

              "&:hover": {
                bgcolor: "#fafbff",
              },
            }}
          >

            {/* INDEX */}
            <TableCell sx={{ py: 3 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#94a3b8",
                }}
              >
                {String(index + 1).padStart(2, "0")}
              </Typography>
            </TableCell>

            {/* SKILL */}
            <TableCell>
              <Chip
                label={q.skill}
                size="small"
                sx={{
                  bgcolor: skillPalette.bg,
                  color: skillPalette.text,
                  border: `1px solid ${skillPalette.border}`,
                  fontWeight: 700,
                  borderRadius: "8px",
                }}
              />
            </TableCell>

            {/* DIFFICULTY */}
            <TableCell>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: .6,
                  borderRadius: "8px",
                  bgcolor: diffCfg.bg,
                  border: `1px solid ${alpha(diffCfg.dot,.15)}`,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: diffCfg.dot,
                  }}
                />

                <Typography
                  sx={{
                    fontWeight: 700,
                    color: diffCfg.text,
                  }}
                >
                  {q.difficulty}
                </Typography>
              </Box>
            </TableCell>

            {/* QUESTION */}
            <TableCell sx={{ maxWidth: 300 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#0f172a",
                }}
              >
                {q.question}
              </Typography>
            </TableCell>

            {/* ANSWER */}
            <TableCell sx={{ maxWidth: 400 }}>
              <Typography
                sx={{
                  color: "#64748b",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {q.ideal_answer || q.answer}
              </Typography>
            </TableCell>

            {/* ACTION */}
            <TableCell>
              <Stack direction="row" spacing={1}>
                <IconButton onClick={() => handleView(q)}>
                  <VisibilityOutlinedIcon />
                </IconButton>

                <IconButton onClick={() => handleEdit(q)}>
                  <EditOutlinedIcon />
                </IconButton>

                <IconButton onClick={() => handleDelete(q.id)}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            </TableCell>

          </TableRow>
        );
      })}
    </TableBody>
  </Table>

</TableContainer>
          </Fade>
        )}
      </Box>

      {/* ── Add/Edit Dialog ── */}
      <Dialog
        open={openAddEdit}
        onClose={() => !isSaving && setOpenAddEdit(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(15,23,42,0.15)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: BRAND.grad,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            >
              <QuizIcon sx={{ fontSize: 16, color: "#fff" }} />
            </Box>
            <Box>
              <Typography fontWeight={700} sx={{ color: "#fff", fontSize: "1rem", letterSpacing: "-0.01em" }}>
                {editingId ? "Edit Question" : "New Question"}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem", fontWeight: 500 }}>
                {editingId ? "Update the details below" : "All fields marked * are required"}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            disabled={isSaving}
            onClick={() => setOpenAddEdit(false)}
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.2)",
              bgcolor: "rgba(255,255,255,0.1)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5, bgcolor: "#f8fafc" }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={<RequiredLabel label="Question" />}
            placeholder="Type the interview question here…"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            error={touched && errors.question}
            helperText={touched && errors.question ? "Question is required" : ""}
            InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
            sx={fieldSx}
            disabled={isSaving}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label={<RequiredLabel label="Expected Answer" />}
            placeholder="Describe the ideal answer or key points…"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            error={touched && errors.answer}
            helperText={touched && errors.answer ? "Expected answer is required" : ""}
            InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
            sx={fieldSx}
            disabled={isSaving}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              label={<RequiredLabel label="Skill" />}
              placeholder="e.g. React.js, Python"
              value={skill}
              onChange={(e) => setSkill(e.target.value)}
              error={touched && errors.skill}
              helperText={touched && errors.skill ? "Skill is required" : ""}
              InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
              sx={fieldSx}
              disabled={isSaving}
            />
            <TextField
              fullWidth
              select
              label={<RequiredLabel label="Difficulty" />}
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              error={touched && errors.difficulty}
              helperText={touched && errors.difficulty ? "Difficulty is required" : ""}
              InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
              sx={fieldSx}
              disabled={isSaving}
            >
              <MenuItem value="" disabled>
                <em>Select difficulty</em>
              </MenuItem>
              {DIFFICULTY_OPTIONS.map((level) => {
                const cfg = getDifficultyConfig(level);
                return (
                  <MenuItem key={level} value={level} sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.dot }} />
                      <span>{level}</span>
                    </Stack>
                  </MenuItem>
                );
              })}
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
          <Button
            disabled={isSaving}
            onClick={() => setOpenAddEdit(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#475569",
              borderRadius: "8px",
              px: 2.5,
              py: 0.75,
              fontSize: "0.85rem",
              border: "1px solid #cbd5e1",
              "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8", color: "#0f172a" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={isSaving}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 0.75,
              fontSize: "0.85rem",
              background: BRAND.grad,
              boxShadow: "0 3px 10px rgba(139,92,246,0.3)",
              "&:hover": { background: BRAND.gradHover, boxShadow: "0 5px 15px rgba(139,92,246,0.4)" },
              "&.Mui-disabled": { background: "#cbd5e1", color: "#f8fafc" }
            }}
          >
            {isSaving ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : editingId ? (
              "Save Changes"
            ) : (
              "Add Question"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View Full Question Dialog ── */}
      <Dialog
        open={openView}
        onClose={() => setOpenView(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(15,23,42,0.15)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "#fff",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                background: BRAND.soft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: BRAND.solid,
              }}
            >
              <QuizIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography fontWeight={700} sx={{ color: "#0f172a", fontSize: "1rem" }}>
              Question Details
            </Typography>
          </Stack>
          <IconButton
            size="small"
            onClick={() => setOpenView(false)}
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              color: "#64748b",
              "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {viewingItem && (
          <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3, bgcolor: "#f8fafc" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              {viewingItem.skill && (
                <Chip
                  label={viewingItem.skill}
                  size="small"
                  sx={{
                    height: 26,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    bgcolor: getSkillPalette(viewingItem.skill).bg,
                    color: getSkillPalette(viewingItem.skill).text,
                    border: `1px solid ${getSkillPalette(viewingItem.skill).border}`,
                    borderRadius: "6px",
                  }}
                />
              )}
              {viewingItem.difficulty && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.25,
                    py: 0.35,
                    borderRadius: "6px",
                    background: getDifficultyConfig(viewingItem.difficulty).bg,
                    border: `1px solid ${alpha(getDifficultyConfig(viewingItem.difficulty).dot, 0.2)}`,
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: getDifficultyConfig(viewingItem.difficulty).dot }} />
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: getDifficultyConfig(viewingItem.difficulty).text }}>
                    {viewingItem.difficulty}
                  </Typography>
                </Box>
              )}
            </Stack>

            <Box>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75 }}>
                The Question
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#0f172a", lineHeight: 1.6 }}>
                {viewingItem.question}
              </Typography>
            </Box>

            <Box
              sx={{
                p: "16px 20px",
                borderRadius: "12px",
                background: BRAND.soft,
                border: `1px solid ${BRAND.border}`,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <LightbulbOutlinedIcon sx={{ fontSize: 18, color: BRAND.solid }} />
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: BRAND.solid, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Expected Answer
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: "0.9rem", color: "#334155", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                {viewingItem.ideal_answer || viewingItem.answer || "No expected answer provided."}
              </Typography>
            </Box>
          </DialogContent>
        )}

        <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fff", borderTop: "1px solid #e2e8f0", justifyContent: "space-between" }}>
          {/* Left side actions (if any), currently empty to push buttons to right */}
          <Box />
          
          <Stack direction="row" spacing={1.5}>
            <Button
              onClick={() => setOpenView(false)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2.5,
                fontSize: "0.85rem",
                color: "#64748b",
                "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" },
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              disableElevation
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={handleEditFromView}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2.5,
                fontSize: "0.85rem",
                bgcolor: BRAND.solid,
                color: "#fff",
                boxShadow: "0 2px 8px rgba(139,92,246,0.25)",
                "&:hover": { bgcolor: BRAND.gradHover, boxShadow: "0 4px 12px rgba(139,92,246,0.35)" },
              }}
            >
              Edit Question
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuestionsPage;

